/* Mera Naksha Design Studio — root page + step orchestrator.
 *
 * Phase 1 MVP flow:
 *   1. Landing hero (free entry, no auth)
 *   2. Plot details (width/depth/facing)
 *   3. Building config (floors/BHK/extras/vastu)
 *   4. Live preview — matched base design + adapted floor plan SVG
 *   5. Checkout (name/WhatsApp/email + Razorpay)
 *   6. Success — downloads + 3D elevation (poll for AI gen)
 *
 * Each step lives in its own component under /studio/* for tractability;
 * orchestration + shared state stays here.
 */
import React, { useCallback, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Compass, Sparkles, ChevronRight } from "lucide-react";

import StepPlot from "@/studio/StepPlot";
import StepBuilding from "@/studio/StepBuilding";
import StepPreview from "@/studio/StepPreview";
import StepCheckout from "@/studio/StepCheckout";
import StepSuccess from "@/studio/StepSuccess";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_REQ = {
  plot_width: 30,
  plot_depth: 40,
  facing: "east",
  floors: "G+1",
  bhk: 3,
  extras: ["parking"],
  vastu: true,
};

export default function DesignStudio() {
  const [step, setStep] = useState("landing"); // landing | plot | building | preview | checkout | success
  const [req, setReq] = useState(DEFAULT_REQ);
  const [preview, setPreview] = useState(null);
  const [checkoutResult, setCheckoutResult] = useState(null);

  const progress = useMemo(() => {
    const order = ["landing", "plot", "building", "preview", "checkout", "success"];
    const i = order.indexOf(step);
    return Math.round((i / (order.length - 1)) * 100);
  }, [step]);

  const fetchPreview = useCallback(async (override = null) => {
    const body = override || req;
    const { data } = await axios.post(`${API}/studio/preview`, body);
    setPreview(data);
    return data;
  }, [req]);

  if (step === "landing") {
    return <StudioLanding onStart={() => setStep("plot")} />;
  }

  return (
    <div className="min-h-screen bg-sand-alt" data-testid="design-studio">
      {/* Header */}
      <header className="bg-navy text-white sticky top-0 z-30 border-b border-navy-600">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="text-[11px] uppercase tracking-[0.18em] font-bold text-gold hover:text-white" data-testid="studio-back-home">
            ← Mera Naksha
          </Link>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.2em] text-gold-dark font-bold">Mera Naksha</div>
            <div className="text-sm sm:text-base font-extrabold">Design Studio</div>
          </div>
          <div className="w-20 text-right text-[11px] text-white/70 tabular-nums">{progress}%</div>
        </div>
        <div className="h-1 bg-navy-600">
          <div
            className="h-1 bg-gold transition-all"
            style={{ width: `${progress}%` }}
            data-testid="studio-progress-bar"
          />
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-8">
        {step === "plot" && (
          <StepPlot
            value={req}
            onChange={setReq}
            onNext={() => setStep("building")}
          />
        )}
        {step === "building" && (
          <StepBuilding
            value={req}
            onChange={setReq}
            onBack={() => setStep("plot")}
            onNext={async () => {
              await fetchPreview();
              setStep("preview");
            }}
          />
        )}
        {step === "preview" && preview && (
          <StepPreview
            req={req}
            preview={preview}
            onBack={() => setStep("building")}
            onContinue={() => setStep("checkout")}
            onRefresh={async (newReq) => {
              setReq(newReq);
              await fetchPreview(newReq);
            }}
          />
        )}
        {step === "checkout" && preview && (
          <StepCheckout
            req={req}
            preview={preview}
            onBack={() => setStep("preview")}
            onPaid={(result) => {
              setCheckoutResult(result);
              setStep("success");
            }}
          />
        )}
        {step === "success" && checkoutResult && (
          <StepSuccess result={checkoutResult} preview={preview} />
        )}
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Landing hero
 * ------------------------------------------------------------------ */
const StudioLanding = ({ onStart }) => (
  <div className="min-h-screen bg-navy-jaali text-white" data-testid="studio-landing">
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 sm:py-14">
      <Link
        to="/"
        className="text-[11px] uppercase tracking-[0.18em] font-bold text-gold/90 hover:text-gold"
        data-testid="studio-back-home"
      >
        ← Back to Mera Naksha
      </Link>

      <div className="mt-6 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold/15 border border-gold/40 text-gold text-[11px] font-bold uppercase tracking-[0.2em]">
        <Sparkles className="h-3.5 w-3.5" /> New · Premium
      </div>

      <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
        Mera Naksha
        <br />
        <span className="text-gold">Design Studio</span>
      </h1>

      <p className="mt-3 text-base sm:text-lg text-white/80 max-w-2xl">
        Smart House Design Powered by Mera Naksha
      </p>

      <p className="mt-5 text-sm sm:text-base text-white/70 max-w-2xl leading-relaxed">
        Enter your plot size and requirements. The Studio automatically picks
        the closest matching design from the Mera Naksha Library, scales every
        room to your dimensions, and generates a fresh 2D floor plan plus a
        photoreal 3D elevation — in under a minute.
      </p>

      {/* Feature pills */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-3xl">
        {[
          ["41+", "Base designs"],
          ["< 60s", "Generation time"],
          ["PDF + PNG", "Downloadable"],
          ["₹999", "Per design"],
        ].map(([n, l]) => (
          <div key={l} className="rounded-2xl bg-white/8 border border-white/15 px-3 py-2.5 backdrop-blur-sm">
            <div className="text-lg sm:text-xl font-extrabold text-gold tabular-nums">{n}</div>
            <div className="text-[10.5px] uppercase tracking-wider text-white/70 font-bold">{l}</div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={onStart}
        data-testid="studio-start-btn"
        className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gold text-navy font-extrabold text-base hover:bg-gold-dark hover:text-white transition-colors shadow-[0_18px_36px_-12px_rgba(212,175,55,0.5)]"
      >
        <Compass className="h-5 w-5" />
        Design my house
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* How it works */}
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-4xl">
        {[
          ["1", "Enter plot details", "Width, depth, facing direction."],
          ["2", "Pick configuration", "Floors, BHK, parking, pooja, more."],
          ["3", "Download instantly", "2D floor plan + AI 3D elevation."],
        ].map(([num, title, body]) => (
          <div key={num} className="rounded-3xl bg-white/8 border border-white/15 p-5 backdrop-blur-sm">
            <div className="text-3xl font-extrabold text-gold tabular-nums leading-none">{num}</div>
            <div className="mt-2 text-sm font-extrabold uppercase tracking-wider text-white">{title}</div>
            <div className="mt-1 text-[13px] text-white/70 leading-snug">{body}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);
